import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useInView } from "framer-motion";
import { Toaster } from "sonner";
import {
  Sparkles, Zap, Shield, Globe, TrendingUp, Bot, ArrowRight, Play,
  Brain, Wallet, Briefcase, GraduationCap, Store, Star, Check
} from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { TiltCard, Particles } from "@/components/glass/TiltCard";
import { useTheme, ThemeToggle } from "@/contexts/ThemeContext";

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease, delay }}
    >
      {children}
    </motion.div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "py-3" : "py-5"}`}>
      <div className={`mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 transition-all duration-300 ${scrolled ? "glass-near rounded-2xl py-3 backdrop-blur-2xl" : ""}`}>
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <Logo className="h-9 w-9" />
          <span className="font-display text-lg font-extrabold tracking-tight">FUN<span className="grad-text">Z</span></span>
        </Link>
        <div className="hidden items-center gap-8 md:flex">
          {["Features", "Platform", "Pricing", "AI"].map((l) => (
            <a key={l} href={`#${l.toLowerCase()}`} data-testid={`nav-${l.toLowerCase()}`} className="text-sm font-medium text-slate-300 transition-colors hover:text-white">{l}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/login" data-testid="nav-login" className="btn-secondary !px-4 !py-2 text-sm">Log in</Link>
          <Link to="/onboarding" data-testid="nav-cta" className="btn-primary !px-4 !py-2 text-sm">Get started</Link>
        </div>
      </div>
    </nav>
  );
};

const Hero = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const { innerWidth, innerHeight } = window;
    setMouse({ x: (e.clientX / innerWidth - 0.5) * 20, y: (e.clientY / innerHeight - 0.5) * 20 });
  };
  return (
    <section onMouseMove={onMove} className="grid-bg relative flex min-h-screen items-center overflow-hidden pt-24">
      <Particles count={40} />
      <div className="pointer-events-none absolute inset-0">
        <div className="parallax-slow absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#7B61FF]/20 blur-[120px]" style={{ transform: `translate(${mouse.x}px, ${mouse.y}px)` }} />
        <div className="parallax-fast absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-[#00D4FF]/20 blur-[120px]" style={{ transform: `translate(${-mouse.x}px, ${-mouse.y}px)` }} />
      </div>
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md">
            <Sparkles className="h-4 w-4 text-[#00FFD1]" />
            <span className="text-xs font-mono uppercase tracking-widest text-slate-300">AI Super-App for Education, Careers & Business</span>
          </div>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            One Platform.<br />
            <span className="grad-text">Every Opportunity.</span>
          </h1>
          <p className="mt-6 max-w-lg text-lg text-slate-400">
            Learn, earn, fund and grow — students, chefs, businesses and enterprises on a single AI-powered platform.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/onboarding" data-testid="hero-cta" className="btn-primary">Start free <ArrowRight className="h-4 w-4" /></Link>
            <a href="#demo" data-testid="hero-demo" className="btn-secondary"><Play className="h-4 w-4" /> Watch demo</a>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#00FFD1]" /> 12,000+ users</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#00FFD1]" /> 13 countries</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#00FFD1]" /> Enterprise-grade</span>
          </div>
        </Reveal>
        <Reveal delay={0.2} className="relative">
          <TiltCard className="mx-auto max-w-md">
            <div className="glass-near relative rounded-3xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Revenue today</p>
                  <p className="mt-1 font-display text-4xl font-extrabold">₹2.4L</p>
                </div>
                <div className="kpi-pulse" />
              </div>
              <div className="mt-6 flex h-32 items-end gap-2">
                {[40, 65, 45, 80, 55, 90, 70, 95, 75, 100, 85, 110].map((h, i) => (
                  <motion.div
                    key={i}
                    className="flex-1 rounded-t-lg bg-gradient-to-t from-[#7B61FF] to-[#00D4FF]"
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.05, ease }}
                  />
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[["Orders", "1,240"], ["Growth", "+24%"], ["Rating", "4.9★"]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl border border-white/10 bg-white/5 p-3 text-center">
                    <p className="font-display text-lg font-bold">{v}</p>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </TiltCard>
        </Reveal>
      </div>
    </section>
  );
};

const features = [
  { icon: Brain, title: "AI Career Advisor", desc: "FUNZI recommends courses, jobs, loans and countries tailored to your profile.", color: "#00D4FF" },
  { icon: GraduationCap, title: "Student Hub", desc: "Courses, scholarships, internships and campus life in one place.", color: "#7B61FF" },
  { icon: Store, title: "Business Cloud", desc: "Kitchens, restaurants, hotels and stores get their own command center.", color: "#00FFD1" },
  { icon: Wallet, title: "FundHub", desc: "Loans, grants, scholarships and investor pitches with dual-approval safety.", color: "#00D4FF" },
  { icon: Briefcase, title: "Career Portal", desc: "AI-matched jobs, hiring marketplaces and payroll in one flow.", color: "#7B61FF" },
  { icon: Shield, title: "Trust & Compliance", desc: "Gov-ID verification, audit trails, fraud alerts and governance built-in.", color: "#00FFD1" },
];

const Features = () => (
  <section id="features" className="relative py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Everything you need</p>
        <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Built for the <span className="grad-text">future of work</span></h2>
      </Reveal>
      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.08}>
            <TiltCard>
              <div className="glass rounded-3xl p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${f.color}20`, boxShadow: `0 0 20px ${f.color}40` }}>
                  <f.icon className="h-6 w-6" style={{ color: f.color }} />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{f.desc}</p>
              </div>
            </TiltCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const DashboardPreview = () => (
  <section id="platform" className="relative py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Live dashboard</p>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Your business,<br /><span className="grad-text">holographic</span></h2>
          <p className="mt-4 text-slate-400">Real-time KPIs, AI predictions, funding, hiring and compliance — all in one glass cockpit.</p>
          <ul className="mt-6 space-y-3">
            {["Revenue analytics with growth forecasts", "AI business coach with confidence scores", "Deal rooms, term sheets and document sharing", "Audit trails and compliance reports"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                <Check className="h-4 w-4 shrink-0 text-[#00FFD1]" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="relative">
            <div className="glass-far absolute -inset-4 rounded-[2rem]" />
            <TiltCard>
              <div className="glass-near relative rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Business Health</p>
                    <p className="mt-1 font-display text-5xl font-extrabold">92<span className="text-xl text-slate-500">/100</span></p>
                  </div>
                  <div className="rounded-2xl border border-[#00FFD1]/30 bg-[#00FFD1]/10 px-4 py-2">
                    <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Excellent</p>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[["Revenue", "₹4.2L", "+18%"], ["Orders", "2,140", "+32"], ["Pending", "₹18K", "3"], ["Team", "12", "+2"]].map(([l, v, d]) => (
                    <div key={l} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="font-display text-2xl font-bold">{v}</p>
                      <div className="mt-1 flex items-center justify-between">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
                        <span className="text-[10px] font-bold text-[#00FFD1]">{d}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TiltCard>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const testimonials = [
  { name: "Priya Sharma", role: "Cloud Kitchen Owner, Hyderabad", text: "FUNZ turned my side hustle into a real business. The funding and analytics changed everything.", rating: 5 },
  { name: "Arjun Mehta", role: "Student, IIT Delhi", text: "I found my scholarship, internship and first job all in one app. The AI advisor is scary accurate.", rating: 5 },
  { name: "Fatima Al-Rashid", role: "Restaurant Manager, Dubai", text: "Hiring, payroll and compliance in one dashboard. My team finally has one source of truth.", rating: 5 },
];

const Testimonials = () => (
  <section className="relative py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Loved worldwide</p>
        <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Trusted by <span className="grad-text">builders</span></h2>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {testimonials.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <TiltCard>
              <div className="glass rounded-3xl p-6">
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="h-4 w-4 fill-[#00FFD1] text-[#00FFD1]" />)}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">“{t.text}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#00D4FF] to-[#7B61FF] font-display text-sm font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            </TiltCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const pricing = [
  { name: "Starter", price: "Free", desc: "For students and explorers", features: ["AI advisor", "Job portal", "Scholarship finder", "Community"], cta: "Get started", popular: false },
  { name: "Pro", price: "$15", period: "/mo", desc: "For professionals & chefs", features: ["Everything in Starter", "Advanced analytics", "Funding marketplace", "Priority support", "Deal rooms"], cta: "Go Pro", popular: true },
  { name: "Enterprise", price: "$199", period: "/mo", desc: "For businesses & hotels", features: ["Everything in Pro", "Team dashboards", "Compliance reports", "SAP/Zoho sync", "Dedicated manager"], cta: "Contact sales", popular: false },
];

const Pricing = () => (
  <section id="pricing" className="relative py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Simple pricing</p>
        <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Start free, <span className="grad-text">scale fast</span></h2>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {pricing.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1}>
            <TiltCard>
              <div className={`glass relative rounded-3xl p-6 ${p.popular ? "border-[#00D4FF]/50 shadow-[0_0_40px_rgba(0,212,255,0.2)]" : ""}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-4 py-1 text-xs font-bold uppercase tracking-widest">Most popular</span>}
                <p className="font-display text-lg font-bold">{p.name}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-extrabold">{p.price}</span>
                  {p.period && <span className="text-slate-500">{p.period}</span>}
                </div>
                <p className="mt-2 text-sm text-slate-400">{p.desc}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-slate-300">
                      <Check className="h-4 w-4 shrink-0 text-[#00FFD1]" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/onboarding" data-testid={`pricing-${p.name.toLowerCase()}`} className={`mt-8 block text-center ${p.popular ? "btn-primary" : "btn-secondary"}`}>{p.cta}</Link>
              </div>
            </TiltCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AIDemo = () => (
  <section id="ai" className="relative py-24">
    <div className="mx-auto max-w-4xl px-4 sm:px-6">
      <Reveal className="text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Meet FUNZI</p>
        <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Your AI <span className="grad-text">co-pilot</span></h2>
      </Reveal>
      <Reveal delay={0.2} className="mt-12">
        <TiltCard>
          <div className="glass-near rounded-3xl p-6">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00D4FF] to-[#7B61FF]">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-display text-sm font-bold">FUNZI AI</p>
                <p className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-[#00FFD1]" /> Online</p>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] p-4 text-sm text-white">
                I'm 18, from India, interested in culinary arts.
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
                <p className="font-bold text-white">Here's your path:</p>
                <ul className="mt-2 space-y-1.5">
                  <li>✅ Chef courses on FUNZ Academy</li>
                  <li>✅ Global Chef Scholarship — ₹2L</li>
                  <li>✅ Student loan options from 7 lenders</li>
                  <li>✅ Internship at cloud kitchens near you</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <input data-testid="ai-input" placeholder="Ask FUNZI anything…" className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#00D4FF]/60" />
              <button data-testid="ai-send" className="btn-primary !px-4 !py-3">Send</button>
            </div>
          </div>
        </TiltCard>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="relative border-t border-white/10 py-12">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-6">
      <div className="flex items-center gap-2">
        <Logo className="h-8 w-8" />
        <span className="font-display text-lg font-extrabold">FUN<span className="grad-text">Z</span></span>
      </div>
      <p className="text-xs text-slate-500">One Platform. Every Opportunity. · © 2026 FUNZ</p>
      <div className="flex gap-6 text-sm text-slate-400">
        <a href="#features" className="hover:text-white">Features</a>
        <a href="#pricing" className="hover:text-white">Pricing</a>
        <Link to="/rules" className="hover:text-white">Rules</Link>
      </div>
    </div>
  </footer>
);

export default function Landing3D() {
  useEffect(() => {
    document.title = "FUNZ — One Platform. Every Opportunity.";
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <div data-testid="landing-3d" className="min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)] antialiased">
      <div className="noise-overlay" aria-hidden="true" />
      <Navbar />
      <main>
        <Hero />
        <Features />
        <DashboardPreview />
        <Testimonials />
        <Pricing />
        <AIDemo />
      </main>
      <Footer />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
